import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { fetchStatusUnreadCount } from '../../lib/statusApi';
import { useAuth } from '../../contexts/AuthContext';
import { useBusiness } from '../../contexts/BusinessContext';
import { useCRMAuth } from '../../contexts/CRMAuthContext';
import { normalizeBusinessRole, canReadTickets } from '../../lib/role';
import { useServiceDegraded } from '../ServiceStatusBanner';
import { useLanguage } from '../../contexts/LanguageContext';
import LanguageSelector from '../Common/LanguageSelector';
import TenantSelector from '../Auth/TenantSelector';
import { fetchMyMemberships, switchTenant } from '../../lib/tenantSession';
import { auth as firebaseAuth } from '../../lib/firebase';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Badge } from '../ui/badge';
import { 
  LayoutDashboard,
  Ticket,
  BarChart3,
  Phone,
  Database,
  Users,
  Settings,
  LogOut,
  Headphones,
  Shield,
  UserCog,
  CreditCard,
  Edit,
  Monitor,
  Brain,
  Crown,
  Globe,
  Zap,
  Target,
  Store,
  PhoneCall,
  ContactRound,
  Bell,
  Bot,
  PlayCircle,
  TrendingUp,
  Activity,
  ChevronDown,
  ChevronUp,
  Mic,
  MessageSquare,
  Lightbulb,
  AlertTriangle,
  Tag,
  Calendar,
  Building2
} from 'lucide-react';

// Maps the English menu label (source of truth in getMenuItems) to an i18n key.
// Any label NOT in this map falls through to its original English text — never blank,
// never a raw key. Descriptions (gray subtext) are intentionally left in English for
// this phase (high-visibility labels first).
const NAV_LABEL_KEY = {
  'Overview': 'nav.overview',
  'AI Voice Monitor': 'nav.aiVoiceMonitor',
  'AI Voice Simulator': 'nav.aiVoiceSimulator',
  'AI Chat Simulator': 'nav.aiChatSimulator',
  'CRM VoIP': 'nav.crmVoip',
  'CRM Contacts': 'nav.crmContacts',
  'Marketplace': 'nav.marketplace',
  'Take a Survey': 'nav.takeSurvey',
  'Team Collaboration': 'nav.teamCollaboration',
  'Unified Desktop': 'nav.unifiedDesktop',
  'Tickets': 'nav.tickets',
  'Scheduling': 'nav.scheduling',
  'Scheduling Settings': 'nav.schedulingSettings',
  'Escalations': 'nav.escalations',
  'Solution Bank': 'nav.solutionBank',
  'Customers': 'nav.customers',
  'Operators Management': 'nav.operatorsManagement',
  'Staff Management': 'nav.staffManagement',
  'Ticket Management': 'nav.ticketManagement',
  'Analytics': 'nav.analytics',
  'Category Management': 'nav.categoryManagement',
  'Create Survey': 'nav.createSurvey',
  'Pending Review': 'nav.pendingReview',
  'Showroom': 'nav.showroom',
  'Billing & Subscription': 'nav.billing',
  'Settings': 'common.settings',
  'Data Hub': 'nav.dataHub',
  'AI Performance Dashboard': 'nav.aiPerformance',
  'Director Dashboard': 'nav.directorDashboard',
  'Strategy Dashboard': 'nav.strategyDashboard',
  'Executive Dashboard': 'nav.executiveDashboard',
  'Strategic Dashboard': 'nav.strategicDashboard',
  'Integration Hub': 'nav.integrationHub',
  'IT Dashboard': 'nav.itDashboard',
};

// Maps the English menu-item description (gray subtext, source of truth in getMenuItems) to an
// i18n key. Any description NOT in this map falls through to its original English text.
const NAV_DESC_KEY = {
  'Dashboard overview': 'navdesc.dashboardOverview',
  'Monitor AI voice interactions': 'navdesc.monitorAiVoice',
  'Test AI voice with complex scenarios': 'navdesc.testAiVoiceComplex',
  'Make integrated calls': 'navdesc.makeIntegratedCalls',
  'Manage your contacts': 'navdesc.manageContacts',
  'Browse products and services': 'navdesc.browseProducts',
  'Participate in surveys': 'navdesc.participateSurveys',
  'Collaborate with team members': 'navdesc.collaborateTeam',
  'All-in-one agent interface': 'navdesc.allInOneAgent',
  'Manage customer tickets': 'navdesc.manageCustomerTickets',
  'Appointments & bookings': 'navdesc.manageScheduling',
  'Hours, types, providers, policies': 'navdesc.schedulingSettings',
  'Handle escalated conversations': 'navdesc.handleEscalated',
  'View solutions': 'navdesc.viewSolutions',
  'AI voice with 30+ scenarios': 'navdesc.aiVoice30',
  'AI chat with 30+ scenarios': 'navdesc.aiChat30',
  'Customer management': 'navdesc.customerManagement',
  'Monitor team performance': 'navdesc.monitorTeamPerf',
  'Manage team members & roles': 'navdesc.manageTeamRoles',
  'Advanced ticket operations': 'navdesc.advancedTicketOps',
  'Approve or reject submitted solutions': 'navdesc.approveReject',
  'Manage feedback categories': 'navdesc.manageFeedbackCats',
  'Reports, insights & AI metrics': 'navdesc.reportsInsights',
  'Manage business showroom': 'navdesc.manageShowroom',
  'Create customer surveys': 'navdesc.createSurveys',
  'Unified data access': 'navdesc.unifiedDataAccess',
  'AI performance analytics': 'navdesc.aiPerfAnalytics',
  'Advanced reporting & AI metrics': 'navdesc.advancedReporting',
  'Team & resource management': 'navdesc.teamResourceMgmt',
  'Strategic insights': 'navdesc.strategicInsights',
  'Strategic analytics & AI metrics': 'navdesc.strategicAnalytics',
  'C-level insights': 'navdesc.cLevelInsights',
  'Revenue & ROI analysis': 'navdesc.revenueRoi',
  'Executive reporting & AI metrics': 'navdesc.executiveReporting',
  'API & system management': 'navdesc.apiSystemMgmt',
  'Performance & system logs': 'navdesc.perfSystemLogs',
  'System analytics & AI metrics': 'navdesc.systemAnalytics',
  'System configuration': 'navdesc.systemConfig',
  'Manage team members': 'navdesc.manageTeamMembers',
  'Plan, invoices, upgrade': 'navdesc.planInvoices',
  'All ticket access': 'navdesc.allTicketAccess',
  'Knowledge base': 'navdesc.knowledgeBase',
  'Customer oversight': 'navdesc.customerOversight',
  'Full analytics & AI metrics': 'navdesc.fullAnalytics',
};

const BusinessSidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { currentBusiness } = useBusiness();
  const { makeAuthenticatedRequest } = useCRMAuth();
  const [openDropdowns, setOpenDropdowns] = useState({});
  // Status & Updates unread dot (subtle; fail-open — no dot on error). Refreshes on navigation so it
  // clears after the user opens the Status page (which marks content seen).
  const [statusUnread, setStatusUnread] = useState(0);
  useEffect(() => { fetchStatusUnreadCount().then(setStatusUnread).catch(() => {}); }, [location.pathname]);
  const serviceDegraded = useServiceDegraded();
  const { t } = useLanguage();

  // PMT1 — a verified person may hold memberships in several businesses. The switch control is
  // rendered ONLY when this person genuinely has more than one; a single-membership user never sees
  // it. The list is the backend's safe projection ({brand_id, business_name}) for THIS identity.
  const [memberships, setMemberships] = useState([]);
  const [showTenantSwitch, setShowTenantSwitch] = useState(false);
  const [switchError, setSwitchError] = useState(null);
  const getTenantToken = useCallback(async () => {
    try { if (firebaseAuth.currentUser) return await firebaseAuth.currentUser.getIdToken(); } catch { /* fall through */ }
    return localStorage.getItem('crm_access_token') || '';
  }, []);
  useEffect(() => {
    let cancelled = false;
    if (!user || normalizeBusinessRole(user?.role) === 'customer') return undefined;
    fetchMyMemberships(getTenantToken)
      .then((r) => { if (!cancelled) setMemberships(r.memberships || []); })
      .catch(() => { /* fail quiet — the control simply stays hidden */ });
    return () => { cancelled = true; };
  }, [user, getTenantToken]);
  const handleSwitchTenant = async (brandId) => {
    setSwitchError(null);
    try {
      // switchTenant re-verifies with the SERVER, purges tenant-scoped state, then reloads the shell.
      await switchTenant(getTenantToken, brandId);
    } catch (err) {
      setSwitchError(err && err.message === 'membership_denied'
        ? t('tenantSelect.denied') : t('tenantSelect.failed'));
    }
  };

  // Authoritative "Active Tickets" metric — open/unresolved tickets for THIS tenant, from a single
  // tenant-scoped endpoint (never hardcoded, never the stale analytics.totalTickets that read 0).
  // null = not yet loaded (shown as an em-dash; we never fabricate a count).
  const [activeTickets, setActiveTickets] = useState(null);
  const brandId = currentBusiness?.brandId;
  // IT/Integration (and any non-ticket role) is not authorized to read tickets — the backend
  // guard 403s the count endpoint. Presentation must MATCH that authority: don't issue a
  // predictably-forbidden request, and hide the widget rather than show a mysterious em-dash.
  const ticketsAuthorized = canReadTickets(user?.role);
  const refreshActiveTickets = useCallback(async () => {
    if (!ticketsAuthorized) return;               // unauthorized role — never call a forbidden endpoint
    if (!brandId || typeof makeAuthenticatedRequest !== 'function') return;
    try {
      const resp = await makeAuthenticatedRequest(`/api/v1/tickets/business/${brandId}/active-count`);
      if (!resp.ok) return;                       // keep last-known count; never zero it out on error
      const data = await resp.json();
      if (typeof data?.active_tickets === 'number') setActiveTickets(data.active_tickets);
    } catch {
      /* transient — keep last-known count, no fabrication */
    }
  }, [ticketsAuthorized, brandId, makeAuthenticatedRequest]);

  // Load on mount / tenant change, and refresh whenever a ticket mutation is broadcast.
  useEffect(() => {
    refreshActiveTickets();
    const onChanged = () => refreshActiveTickets();
    window.addEventListener('tickets:changed', onChanged);
    return () => window.removeEventListener('tickets:changed', onChanged);
  }, [refreshActiveTickets]);

  // Localize a menu label if it has a known translation key; otherwise keep the English label.
  const tLabel = (label) => (NAV_LABEL_KEY[label] ? t(NAV_LABEL_KEY[label]) : label);
  // Localize a menu-item description; otherwise keep the English description.
  const tDesc = (desc) => (NAV_DESC_KEY[desc] ? t(NAV_DESC_KEY[desc]) : desc);

  const roleDefinitions = {
    admin: { title: 'Administrator', icon: Shield, color: 'bg-purple-100 text-purple-800' },
    operator: { title: 'Operator', icon: Headphones, color: 'bg-blue-100 text-blue-800' },
    manager: { title: 'Manager', icon: Users, color: 'bg-orange-100 text-orange-800' },
    analyst: { title: 'Analyst', icon: BarChart3, color: 'bg-teal-100 text-teal-800' },
    director: { title: 'Director', icon: Target, color: 'bg-indigo-100 text-indigo-800' },
    executive: { title: 'Executive', icon: Crown, color: 'bg-rose-100 text-rose-800' },
    it: { title: 'IT/Integration', icon: Globe, color: 'bg-gray-100 text-gray-800' },
    super_admin: { title: 'Super Admin', icon: Crown, color: 'bg-red-100 text-red-800' }
  };

  // Enhanced role-based menu items
  const getMenuItems = (userRole) => {
    const baseItems = [
      {
        icon: LayoutDashboard,
        label: 'Overview',
        path: '/business',
        description: 'Dashboard overview',
        roles: ['customer', 'admin', 'operator', 'manager', 'analyst', 'director', 'executive', 'it', 'super_admin']
      },
    ];

    // Servisfy Status & Updates — universal to every Business role (the sole
    // all_registered_businesses feature). MOVED OUT OF baseItems so it renders as the LAST Business
    // navigation item instead of the second. Appended unconditionally below, exactly as it was
    // unconditional inside baseItems, so visibility is byte-for-byte unchanged: every role that saw
    // it before still sees it. `roles` is carried along untouched — it is vestigial metadata here
    // (nothing filters on it), and dropping it would be an unrelated change.
    const statusUpdatesItem = {
      icon: Bell,
      label: 'Status & Updates',
      path: '/business/status',
      description: 'Platform status, incidents & what’s new',
      roles: ['customer', 'admin', 'operator', 'manager', 'analyst', 'director', 'executive', 'it', 'super_admin']
    };

    const roleSpecificItems = {
      customer: [
        {
          icon: Bot,
          label: 'AI Voice Monitor',
          path: '/business/ai-voice-monitor',
          description: 'Monitor AI voice interactions'
        },
        {
          icon: PlayCircle,
          label: 'AI Voice Simulator',
          path: '/business/ai-voice-simulator',
          description: 'Test AI voice with complex scenarios'
        },
        {
          icon: PhoneCall,
          label: 'CRM VoIP',
          path: '/business/crm-voip',
          description: 'Make integrated calls'
        },
        {
          icon: ContactRound,
          label: 'CRM Contacts',
          path: '/business/crm-contacts',
          description: 'Manage your contacts'
        },
        {
          icon: Store,
          label: 'Marketplace',
          path: '/business/marketplace',
          description: 'Browse products and services'
        }
      ],
      // Operator authorized-nav order (P1-A): 1 Overview (baseItems) 2 Unified Desktop 3 Tickets
      // 4 Escalations 5 Team Collaboration 6 Scheduling 7 Solution Bank 8 AI Voice Simulator
      // 9 AI Chat Simulator 10 Customers. Same list drives desktop + mobile drawer.
      operator: [
        {
          icon: Monitor,
          label: 'Unified Desktop',
          path: '/business/unified-desktop',
          description: 'All-in-one agent interface'
        },
        {
          icon: Ticket,
          label: 'Tickets',
          path: '/business/tickets',
          description: 'Manage customer tickets'
        },
        {
          icon: AlertTriangle,
          label: 'Escalations',
          path: '/business/escalations',
          description: 'Handle escalated conversations'
        },
        {
          icon: PhoneCall,
          label: 'Team Collaboration',
          path: '/business/voice-calls',
          description: 'Collaborate with team members'
        },
        {
          icon: Calendar,
          label: 'Scheduling',
          path: '/business/scheduling',
          description: 'Appointments & bookings'
        },
        {
          icon: Database,
          label: 'Solution Bank',
          path: '/business/solution-bank',
          description: 'View solutions'
        },
        {
          icon: PlayCircle,
          label: 'AI Voice Simulator',
          path: '/business/ai-voice-simulator',
          description: 'AI voice with 30+ scenarios'
        },
        {
          icon: MessageSquare,
          label: 'AI Chat Simulator',
          path: '/business/ai-chat-simulator',
          description: 'AI chat with 30+ scenarios'
        },
        {
          icon: Users,
          label: 'Customers',
          path: '/business/customers',
          description: 'Customer management'
        }
      ],
      manager: [
        {
          icon: PhoneCall,
          label: 'Team Collaboration',
          path: '/business/voice-calls',
          description: 'Collaborate with team members'
        },
        {
          icon: Users,
          label: 'Operators Management',
          path: '/business/manage-operators',
          description: 'Monitor team performance'
        },
        {
          icon: UserCog,
          label: 'Staff Management',
          path: '/business/staff-management',
          description: 'Manage team members & roles'
        },
        {
          icon: Ticket,
          label: 'Ticket Management',
          path: '/business/ticket-management',
          description: 'Advanced ticket operations'
        },
        {
          icon: Calendar,
          label: 'Scheduling',
          path: '/business/scheduling',
          description: 'Appointments & bookings'
        },
        {
          icon: Settings,
          label: 'Scheduling Settings',
          path: '/business/scheduling-config',
          description: 'Hours, types, providers, policies'
        },
        {
          icon: AlertTriangle,
          label: 'Escalations',
          path: '/business/escalations',
          description: 'Handle escalated conversations'
        },
        {
          icon: Database,
          label: 'Solution Bank',
          path: '/business/solution-bank',
          description: 'View solutions'
        },
        {
          icon: Tag,
          label: 'Category Management',
          path: '/business/category-manager',
          description: 'Manage feedback categories'
        },
        {
          icon: PlayCircle,
          label: 'AI Voice Simulator',
          path: '/business/ai-voice-simulator',
          description: 'AI voice with 30+ scenarios'
        },
        {
          icon: MessageSquare,
          label: 'AI Chat Simulator',
          path: '/business/ai-chat-simulator',
          description: 'AI chat with 30+ scenarios'
        },
        {
          icon: BarChart3,
          label: 'Analytics',
          path: '/business/analytics',
          description: 'Reports, insights & AI metrics'
        },
        {
          icon: Users,
          label: 'Customers',
          path: '/business/customers',
          description: 'Customer management'
        },
        {
          icon: Store,
          label: 'Showroom',
          path: '/business/showroom',
          description: 'Manage business showroom'
        },
        {
          icon: Edit,
          label: 'Create Survey',
          path: '/business/create-survey',
          description: 'Create customer surveys'
        }
      ],
      analyst: [
        {
          icon: PhoneCall,
          label: 'Team Collaboration',
          path: '/business/voice-calls',
          description: 'Collaborate with team members'
        },
        {
          icon: Database,
          label: 'Data Hub',
          path: '/business/data-hub',
          description: 'Unified data access'
        },
        {
          icon: BarChart3,
          label: 'AI Performance Dashboard',
          path: '/business/analytics-dashboard',
          description: 'AI performance analytics'
        },
        {
          icon: TrendingUp,
          label: 'Analytics',
          path: '/business/analytics',
          description: 'Advanced reporting & AI metrics'
        }
        // Scheduling intentionally NOT shown to analyst — backend SCHED_BOOK_ROLES denies analyst.
      ],
      director: [
        {
          icon: PhoneCall,
          label: 'Team Collaboration',
          path: '/business/voice-calls',
          description: 'Collaborate with team members'
        },
        {
          icon: Target,
          label: 'Director Dashboard',
          path: '/business/director-dashboard',
          description: 'Team & resource management'
        },
        {
          icon: Zap,
          label: 'Strategy Dashboard',
          path: '/business/strategy-dashboard',
          description: 'Strategic insights'
        },
        {
          icon: BarChart3,
          label: 'Analytics',
          path: '/business/analytics',
          description: 'Strategic analytics & AI metrics'
        }
        // Scheduling intentionally NOT shown to director — backend SCHED_BOOK_ROLES denies director.
      ],
      executive: [
        {
          icon: PhoneCall,
          label: 'Team Collaboration',
          path: '/business/voice-calls',
          description: 'Collaborate with team members'
        },
        {
          icon: Crown,
          label: 'Executive Dashboard',
          path: '/business/executive-dashboard',
          description: 'C-level insights'
        },
        {
          icon: Zap,
          label: 'Strategic Dashboard',
          path: '/business/executive-strategic',
          description: 'Revenue & ROI analysis'
        },
        {
          icon: BarChart3,
          label: 'Analytics',
          path: '/business/analytics',
          description: 'Executive reporting & AI metrics'
        }
        // Scheduling intentionally NOT shown to executive — backend SCHED_BOOK_ROLES denies executive.
      ],
      it: [
        {
          icon: PhoneCall,
          label: 'Team Collaboration',
          path: '/business/voice-calls',
          description: 'Collaborate with team members'
        },
        {
          icon: Globe,
          label: 'Integration Hub',
          path: '/business/integration-hub',
          description: 'API & system management'
        },
        {
          icon: Activity,
          label: 'IT Dashboard',
          path: '/business/it-dashboard',
          description: 'Performance & system logs'
        },
        {
          icon: BarChart3,
          label: 'Analytics',
          path: '/business/analytics',
          description: 'System analytics & AI metrics'
        },
        // Scheduling + Scheduling Settings intentionally omitted for IT/Integration: the backend
        // scheduling guards (SCHED_BOOK_ROLES / SCHED_CONFIG_ROLES) exclude 'it', so these routes
        // 403. Navigation must not advertise a resource the role cannot use.
        {
          icon: Settings,
          label: 'Settings',
          path: '/business/settings',
          description: 'System configuration'
        }
      ],
      admin: [
        {
          icon: PhoneCall,
          label: 'Team Collaboration',
          path: '/business/voice-calls',
          description: 'Collaborate with team members'
        },
        {
          icon: UserCog,
          label: 'Staff Management',
          path: '/business/staff-management',
          description: 'Manage team members'
        },
        {
          icon: CreditCard,
          label: 'Billing & Subscription',
          path: '/business/billing',
          description: 'Plan, invoices, upgrade'
        },
        {
          icon: CreditCard,
          label: 'Plan & Capabilities',
          path: '/business/plan-capabilities',
          description: 'Authoritative plan, usage, features & integrations'
        },
        {
          icon: Ticket,
          label: 'Ticket Management',
          path: '/business/tickets',
          description: 'All ticket access'
        },
        {
          icon: Calendar,
          label: 'Scheduling',
          path: '/business/scheduling',
          description: 'Appointments & bookings'
        },
        {
          icon: Settings,
          label: 'Scheduling Settings',
          path: '/business/scheduling-config',
          description: 'Hours, types, providers, policies'
        },
        {
          icon: AlertTriangle,
          label: 'Escalations',
          path: '/business/escalations',
          description: 'Handle escalated conversations'
        },
        {
          icon: Database,
          label: 'Solution Bank',
          path: '/business/solution-bank',
          description: 'Knowledge base'
        },
        {
          icon: Tag,
          label: 'Category Management',
          path: '/business/category-manager',
          description: 'Manage feedback categories'
        },
        {
          icon: PlayCircle,
          label: 'AI Voice Simulator',
          path: '/business/ai-voice-simulator',
          description: 'AI voice with 30+ scenarios'
        },
        {
          icon: MessageSquare,
          label: 'AI Chat Simulator',
          path: '/business/ai-chat-simulator',
          description: 'AI chat with 30+ scenarios'
        },
        {
          icon: BarChart3,
          label: 'Analytics',
          path: '/business/analytics',
          description: 'Full analytics & AI metrics'
        },
        {
          icon: Users,
          label: 'Customers',
          path: '/business/customers',
          description: 'Customer oversight'
        },
        {
          icon: Store,
          label: 'Showroom',
          path: '/business/showroom',
          description: 'Manage business showroom'
        },
        {
          icon: Edit,
          label: 'Create Survey',
          path: '/business/create-survey',
          description: 'Create customer surveys'
        },
        {
          icon: Settings,
          label: 'Settings',
          path: '/business/settings',
          description: 'System configuration'
        }
      ],
      // Owner/SuperAdmin is a SEPARATE plane (/superadmin-portal, OTP/JWT) — no
      // SuperAdmin menu is ever shown inside the Business plane.
    };

    // Status & Updates LAST: after every governed role-specific item, and still above the fixed
    // footer controls (System Status / Active Tickets / Language / Switch business / Logout),
    // which render outside this list and are not touched.
    return [...baseItems, ...(roleSpecificItems[userRole] || []), statusUpdatesItem];
  };

  const menuItems = getMenuItems(normalizeBusinessRole(user?.role));

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path) => {
    if (path === '/business') {
      return location.pathname === '/business' || location.pathname === '/business/';
    }
    return location.pathname.startsWith(path);
  };

  const normalizedRole = normalizeBusinessRole(user?.role);
  const currentRole = roleDefinitions[normalizedRole] || roleDefinitions.operator;
  const RoleIcon = currentRole.icon;
  // Translated role title (falls back to English def title via t()'s English fallback).
  const roleKeyForTitle = roleDefinitions[normalizedRole] ? normalizedRole : 'operator';
  const roleTitle = t(`role.${roleKeyForTitle}`);

  return (
    <div className="w-64 xl:w-72 shrink-0 h-full overflow-hidden bg-white/80 backdrop-blur-sm border-r border-gray-200 flex flex-col">
      {/* Header */}
      <div className="shrink-0 p-4 border-b border-gray-200">
        <div className="flex items-center space-x-3">
          <Phone className="h-8 w-8 text-purple-600" />
          <div>
            <h1 className="text-xl font-bold text-gray-900">Servisfy</h1>
            <p className="text-sm text-gray-500">{t('misc.businessPortal')}</p>
          </div>
        </div>
      </div>

      {/* Business Info */}
      {currentBusiness && (
        <div className="shrink-0 px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-green-50 to-purple-50">
          <div className="flex items-center space-x-3">
            <Avatar className="h-9 w-9 shrink-0">
              <AvatarFallback className="bg-gradient-to-r from-blue-400 to-purple-500 text-white font-semibold">
                {(currentBusiness.businessName || 'Business').charAt(0)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-gray-900 truncate">{currentBusiness.businessName || 'Business'}</p>
              {/* DR-008/gap-J: never surface the raw brand/business ID to tenant users — the business
                  NAME above is the identity; the canonical id stays internal. */}
            </div>
          </div>
        </div>
      )}

      {/* User Profile */}
      <div className="shrink-0 px-4 py-3 border-b border-gray-200">
        <div className="flex items-center space-x-3">
          <Avatar className="h-10 w-10 shrink-0">
            <AvatarFallback className="bg-gradient-to-r from-green-400 to-purple-500 text-white font-semibold">
              {user?.name?.charAt(0).toUpperCase() || 'U'}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-gray-900 truncate">{user?.name || roleTitle}</p>
            <p className="text-sm text-gray-500 truncate">{user?.email || user?.phone || ''}</p>
            <Badge className={`mt-1 ${currentRole.color}`} variant="secondary">
              <RoleIcon className="h-3 w-3 mr-1" />
              {roleTitle}
            </Badge>
          </div>
        </div>
      </div>

      {/* Navigation Menu — min-h-0 lets this flex child shrink so overflow-y-auto engages and the
          logout/status footer below stays reachable on short viewports with many menu items. */}
      <nav className="flex-1 basis-0 min-h-0 px-3 py-2 overflow-y-auto overflow-x-hidden">
        <div className="space-y-1">
          {menuItems.map((item, index) => {
            const IconComponent = item.icon;
            
            // Handle dropdown menus
            if (item.isDropdown && item.submenus) {
              const isDropdownOpen = openDropdowns[item.label];
              const isAnySubmenuActive = item.submenus.some(submenu => isActive(submenu.path));
              
              return (
                <div key={item.label}>
                  <Button
                    variant={isAnySubmenuActive ? "secondary" : "ghost"}
                    className={`w-full justify-start h-auto px-3 py-2.5 ${
                      isAnySubmenuActive
                        ? 'bg-gradient-to-r from-green-100 to-purple-100 border border-purple-200' 
                        : 'hover:bg-green-50'
                    }`}
                    onClick={() => setOpenDropdowns(prev => ({ ...prev, [item.label]: !prev[item.label] }))}
                  >
                    <IconComponent className={`h-5 w-5 mr-3 shrink-0 ${
                      isAnySubmenuActive ? 'text-purple-600' : 'text-gray-500'
                    }`} />
                    <div className="text-left flex-1 min-w-0 whitespace-normal break-words">
                      <div className={`font-medium ${
                        isAnySubmenuActive ? 'text-purple-900' : 'text-gray-700'
                      }`}>
                        {tLabel(item.label)}
                      </div>
                    </div>
                    {isDropdownOpen ? (
                      <ChevronUp className="h-4 w-4 text-gray-500" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-gray-500" />
                    )}
                  </Button>
                  
                  {/* Submenu items */}
                  {isDropdownOpen && (
                    <div className="ml-4 mt-1 space-y-1">
                      {item.submenus.map(submenu => {
                        const SubmenuIcon = submenu.icon;
                        return (
                          <Button
                            key={submenu.path}
                            variant={isActive(submenu.path) ? "secondary" : "ghost"}
                            className={`w-full justify-start h-auto px-3 py-2 ${
                              isActive(submenu.path) 
                                ? 'bg-gradient-to-r from-green-50 to-purple-50 border-l-2 border-purple-400' 
                                : 'hover:bg-green-50'
                            }`}
                            onClick={() => navigate(submenu.path)}
                          >
                            <SubmenuIcon className={`h-4 w-4 mr-3 shrink-0 ${
                              isActive(submenu.path) ? 'text-purple-600' : 'text-gray-400'
                            }`} />
                            <div className="text-left min-w-0 flex-1 whitespace-normal break-words">
                              <div className={`text-sm font-medium ${
                                isActive(submenu.path) ? 'text-purple-900' : 'text-gray-700'
                              }`}>
                                {tLabel(submenu.label)}
                              </div>
                              <div className="text-xs text-gray-500">{tDesc(submenu.description)}</div>
                            </div>
                          </Button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }
            
            // Handle regular menu items
            return (
              <Button
                key={item.path}
                variant={isActive(item.path) ? "secondary" : "ghost"}
                className={`w-full justify-start h-auto px-3 py-2.5 ${
                  isActive(item.path) 
                    ? 'bg-gradient-to-r from-green-100 to-purple-100 border border-purple-200' 
                    : 'hover:bg-green-50'
                }`}
                onClick={() => navigate(item.path)}
              >
                <IconComponent className={`h-5 w-5 mr-3 shrink-0 ${
                  isActive(item.path) ? 'text-purple-600' : 'text-gray-500'
                }`} />
                {/* The shadcn Button base sets `whitespace-nowrap`, which this block inherits — long
                    labels/descriptions then could not wrap and overflowed the fixed-width rail,
                    forcing a horizontal scrollbar on the nav (which is overflow-y-auto, so the x
                    axis computes to auto). Re-enable wrapping and let the block shrink. */}
                <div className="text-left min-w-0 flex-1 whitespace-normal break-words">
                  <div className={`font-medium ${
                    isActive(item.path) ? 'text-purple-900' : 'text-gray-700'
                  }`}>
                    {tLabel(item.label)}
                  </div>
                  <div className="text-xs text-gray-500">{tDesc(item.description)}</div>
                </div>
                {item.path === '/business/status' && statusUnread > 0 ? (
                  <span
                    className="ml-auto inline-flex h-2.5 w-2.5 rounded-full bg-purple-600"
                    aria-label={`${statusUnread} unread status updates`}
                    title={`${statusUnread} new`}
                  />
                ) : null}
              </Button>
            );
          })}
        </div>
      </nav>

      {/* Status Indicators */}
      <div className="shrink-0 max-h-[45%] overflow-y-auto px-4 py-3 border-t border-gray-200">
        <div className="space-y-1.5 mb-3">
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-600">{t('status.systemStatus')}</span>
            <div className="flex items-center">
              <div className={`w-2 h-2 rounded-full mr-2 ${serviceDegraded ? 'bg-amber-500 animate-pulse' : 'bg-green-400'}`}></div>
              <span className={`font-medium ${serviceDegraded ? 'text-amber-600' : 'text-green-600'}`}>
                {serviceDegraded ? t('status.degraded') : t('common.online')}
              </span>
            </div>
          </div>
          {/* Only render for ticket-authorized roles. 0 renders as 0 (a real count), never fabricated;
              null (still loading) shows an em-dash. Unauthorized roles (e.g. IT/Integration) don't see
              this row at all — no forbidden request, no mystery em-dash. */}
          {ticketsAuthorized && (
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-600">{t('status.activeTickets')}</span>
              <Badge variant="secondary">{activeTickets == null ? '—' : activeTickets}</Badge>
            </div>
          )}
        </div>

        {/* Interface language selector (Web CRM UI only) */}
        <div className="mb-2">
          <LanguageSelector />
        </div>

        {memberships.length > 1 && (
          <div className="mb-2">
            {showTenantSwitch ? (
              <TenantSelector
                memberships={memberships}
                onSelect={handleSwitchTenant}
                error={switchError}
                cancelLabel={t('common.cancel')}
                onCancel={() => { setShowTenantSwitch(false); setSwitchError(null); }}
              />
            ) : (
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => setShowTenantSwitch(true)}
              >
                <Building2 className="h-4 w-4 mr-2" />
                {t('tenantSelect.switch')}
              </Button>
            )}
          </div>
        )}

        <Button
          variant="outline"
          size="sm"
          className="w-full text-red-600 border-red-200 hover:bg-red-50"
          onClick={handleLogout}
        >
          <LogOut className="h-4 w-4 mr-2" />
          {t('common.logout')}
        </Button>
      </div>
    </div>
  );
};

export default BusinessSidebar;